import { syncPdpIdentity } from '@pogi/pdp-refinements';
import { StandardEvents } from '@pogi/standard-events';
import {
  calculateShopifyUnitSavings,
  formatShopifyMoney,
  formatShopifyMoneyWithTemplate,
  formatShopifyUnitMoney,
  formatShopifyWholeMoney,
  resolveShopifyPdpPrice,
} from '@pogi/money';
import { PogiBuyBoxModel, readPogiBuyBoxContract } from '@pogi/buy-box-v2-core';
import {
  PogiBuyBoxHorizonAdapter,
  readPogiBuyBoxNativeState,
  revealPogiBuyBoxNativeControls,
} from '@pogi/buy-box-v2-horizon';
import { bindPdpUsage, normalizeDailyUsage, pdpUsageDuration, formatPdpUsageNumber, pdpUsageEstimate, formatPdpUsageDuration, pdpUsagePriceText, renderPdpUsage, renderPdpIntervalSelect, resolveAvailablePlanSet as resolveConfiguredPlanSet, resolveInitialSellingPlanId } from '@pogi/buy-box-v2-subscription-config';

/*
 * pogi-buy-box-bags — ISOLATED bags variant of pogi-buy-box. Own <pogi-buy-box-bags>
 * element + own file so the bags economics CANNOT touch the shared wipes/pads buy box.
 *
 * 3-AXIS bags model (post-merge 2026-06-06): Format × Size × Scent.
 *   Format (option 1): Refill Rolls | Jumbo Roll | Tie-Handle — rendered as tabs.
 *   Size   (option 2): per-format count ladder (Refill 10/30/50/60; Jumbo 500; Tie 300/900).
 *   Scent  (option 3): Fresh Scented | Unscented — rendered as tiles.
 * The Size value carries a unit count ("10 Rolls (150 Bags)" or bare "300 Bags"), so
 * ¢/bag + SAVE run on BAGS. Selecting (format,size,scent) drives Horizon's hidden
 * native variant-picker by option value (cart/price/media/selling-plan stay native).
 * Special cases: Jumbo shows a descriptor (NOT ¢/bag); the 50-roll Refill is a bundle
 * (+2 dispensers role). Default selection = the format's "Most popular" size + Fresh +
 * Subscribe, driven onto the native picker on load so ATC is correct without interaction.
 */

/* i18n: strings come from the data blob's `i18n` object (Liquid renders them via
   `| t` from locales/*.json pogi.bb.*). EVERY read falls back to the exact English
   literal that used to be hardcoded, so a missing key can never change the EN site.
   [[var]] placeholders (not {{ }}) so Liquid's t-filter never eats them. */
const pogiTpl = (I, k, fb, vars) => {
  let s = (I && I[k]) || fb;
  if (vars) for (const key of Object.keys(vars)) s = s.split('[[' + key + ']]').join(String(vars[key]));
  return s;
};
const pogiSetTranslatedText = (element, value) => {
  if (!element) return;
  if (typeof window.POGI_SET_TRANSLATED_TEXT === 'function') window.POGI_SET_TRANSLATED_TEXT(element, value);
  else element.textContent = value == null ? '' : String(value);
};

/* k = stable role key (i18n lookup + "Most popular" default logic); t = EN fallback */
const ROLE = {
  'refill-rolls': {
    10: { k: 'onthego', t: 'On the go' },
    30: { k: 'popular', t: 'Most popular', accent: true },
    50: { k: 'bundle', t: '+2 dispensers', accent: true },
    60: { k: 'stockup', t: 'Stock up', accent: true },
  },
  'jumbo-roll': { 500: { k: 'bulk', t: 'Bulk roll' } },
  'tie-handle': { 300: { k: 'everyday', t: 'Everyday' }, 900: { k: 'value', t: 'Best value', accent: true } },
};
// Per-format tab descriptor (prototype copy).
const FORMAT_DESC = {
  'refill-rolls': 'On-the-go · fits leash clips',
  'jumbo-roll': 'One large continuous roll',
  'tie-handle': 'Tie-off handles, less mess',
};
// Per-format line glyph — depicts the physical FORMAT (roll / jumbo roll / tie-handle
// bag), not the packaging. Pack photos of these would all read as similar boxes; the
// shape is what differs, so a shape glyph communicates the choice. Lucide stroke style
// (currentColor → the card sets the color), matching pogi-icon + the Pads size-glyphs.
// All three are normalized to the SAME vertical extent (~y1.5–22.5 of the 24 box),
// so the row of cards reads even.
const FORMAT_GLYPH = {
  // Refill = a STACK of rolls (3-2-1 pyramid, end-on) so it reads as "lots of rolls".
  // Circles are TANGENT (hexagonal packing: row offset = r·√3, neighbors = 2r) so the
  // rolls touch like a real stack — floating gaps read wrong (owner 2026-06-12).
  'refill-rolls': '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><circle cx="4.9" cy="18.2" r="3.55"/><circle cx="12" cy="18.2" r="3.55"/><circle cx="19.1" cy="18.2" r="3.55"/><circle cx="8.45" cy="12.05" r="3.55"/><circle cx="15.55" cy="12.05" r="3.55"/><circle cx="12" cy="5.9" r="3.55"/><circle cx="4.9" cy="18.2" r="1"/><circle cx="12" cy="18.2" r="1"/><circle cx="19.1" cy="18.2" r="1"/><circle cx="8.45" cy="12.05" r="1"/><circle cx="15.55" cy="12.05" r="1"/><circle cx="12" cy="5.9" r="1"/></svg>',
  // Jumbo = one big LONG continuous roll — tall + slightly narrower so it reads as length.
  'jumbo-roll': '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><ellipse cx="12" cy="3.5" rx="5.8" ry="2"/><path d="M6.2 3.5v17a5.8 2 0 0 0 11.6 0v-17"/><ellipse cx="12" cy="3.5" rx="1.9" ry="0.7"/></svg>',
  // Tie-Handle = a classic t-shirt grocery bag — rounded shoulders + handles with a deep
  // U-scoop, straight sides with a slight taper, rounded bottom.
  'tie-handle': '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M5.2 20L4.8 11.8Q4.7 11 5.6 11H7V3Q7 1.6 8.5 1.6Q10 1.6 10 3V8.2Q12 10 14 8.2V3Q14 1.6 15.5 1.6Q17 1.6 17 3V11H18.4Q19.3 11 19.2 11.8L18.8 20Q18.7 22.4 16.4 22.5H7.6Q5.3 22.4 5.2 20Z"/></svg>',
};
// Jumbo Roll shows this in place of ¢/bag (headline already carries the bag count).
const JUMBO_DESC = 'For yards and multi-dog homes';
const ICONS = {
  sprout: '<svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 20v-8"/><path d="M12 12c0-3.3 2.7-6 6-6 0 3.3-2.7 6-6 6z"/><path d="M12 14c0-2.8-2.2-5-5-5 0 2.8 2.2 5 5 5z"/></svg>',
  droplet: '<svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 2.6S5 9.2 5 14a7 7 0 0 0 14 0c0-4.8-7-11.4-7-11.4z"/></svg>',
};

/* POGI L10N FIX (2026-07-23): selling-plan names are LOCALIZED per storefront
   locale (9 locales: en/ja/fr/de/es/it + nl/pl/pt queued). The old parsers were
   EN(+JA)-only, so every EU plan name parsed null and the frequency picker fell
   back to the first N product plans = WRONG cadences offered off-EN. Unit-word
   alternations + a no-digit=every-1 branch (fr «tous les mois», de „jeden Monat",
   es «cada mes», pl „co miesiąc", pt «mensal»). Match order month→week→day MUST
   hold: PL week words (tydzień/tygodni) contain the day stems (dzie/dni).
   Trip-tested against all live plan names ×6 locales + nl/pl/pt synthetics. */
const POGI_FREQ_WORDS = {
  month: 'month|か月|ヶ月|mois|monat|maand|miesi|mensil|mensual|mensal|mese|mesi|meses|mês|mes\\b',
  week: 'week|weke|週間|semaine|woche|wochen|settiman|semanas|semana|tygod|tydzi|semanal|wekelijks',
  day: 'day|日ごと|jour|tage|tag\\b|giorn|día|dias|dag\\b|dagen|dzie|dni\\b|dia\\b',
};
const pogiParseFreq = (raw) => {
  const s = String(raw).toLowerCase().replace(/[０-９]/g, (d) => String.fromCharCode(d.charCodeAt(0) - 0xFEE0));
  // Pass 1: explicit "N <unit>" wins; with several numeric clauses the LAST one is
  // the frequency ("1 month supply – delivery every 2 weeks" / "Vorrat für einen
  // Monat – Lieferung alle 2 Wochen" must both parse week:2, never month:1).
  // matchAll (not match, which only sees the FIRST per-unit hit) so SAME-unit
  // compounds also keep the last clause ("2 weeks starter – every 4 weeks" = week:4).
  let best = null;
  for (const u of ['month', 'week', 'day']) {
    for (const m of s.matchAll(new RegExp('(\\d+)\\s*(?:' + POGI_FREQ_WORDS[u] + ')', 'g'))) {
      if (!best || m.index > best.idx) best = { u: u, n: parseInt(m[1], 10), idx: m.index };
    }
  }
  if (best) return { u: best.u, n: best.n };
  // Pass 2: a bare unit word = every-1 (fr «tous les mois», de „jeden Monat", pl „co miesiąc").
  for (const u of ['month', 'week', 'day']) {
    if (new RegExp(POGI_FREQ_WORDS[u]).test(s)) return { u: u, n: 1 };
  }
  return null;
};

// A11Y (audit P3, 2026-07-23): the buy box's radio-group containers, for the
// roving-tabindex pattern. .pogi-bb__purchase and [data-pogi-freq-opts] hold
// role="radio" buttons without a radiogroup role in the Liquid, so they are
// matched explicitly. The sizing modal's radios are deliberately NOT covered —
// it owns its own DOM lifecycle. Selectors absent from a given clone are
// harmless (querySelectorAll/closest simply never match).
const POGI_ROVE_GROUPS = '[data-pogi-formats], [data-pogi-sizes], [data-pogi-tiles], [data-pogi-counts], .pogi-bb__purchase, [data-pogi-freq-opts]';
// Eligible = focusable-for-roving: not disabled, not [hidden], and rendered.
// offsetParent is the visibility check: it is null when the radio OR any
// ancestor is display:none (the clones hide invalid pills via style.display
// and whole panels via [hidden]) — one check covers both, and none of these
// radios are position:fixed (the one case offsetParent cannot see). Chosen
// over getComputedStyle for cost (Codex countersign #19, 2026-07-23).
const POGI_ROVE_ELIGIBLE = (b) => !b.disabled && !b.hidden && b.offsetParent !== null;

class PogiBuyBoxBags extends HTMLElement {
  getDailyUsage() { return normalizeDailyUsage(this.usagePerDay, this.estPerDay || 2); }
  getUsageDuration(units) { return pdpUsageDuration(units, this.getDailyUsage(), 'months'); }
  setDailyUsage(value) {
    if (value != null && String(value).trim() && Number.isFinite(Number(value))) this.pogiUsageEdited = true;
    this.usagePerDay = normalizeDailyUsage(value, this.getDailyUsage());
    this.update();
    return this.usagePerDay;
  }
  usageNumber(value) { return formatPdpUsageNumber(value, this.v2Contract?.market?.displayLocale); }
  getUsageEstimate(units) { return pdpUsageEstimate(units, this.getDailyUsage(), 'months'); }
  usageDurationText(estimate) { return formatPdpUsageDuration(estimate, this.v2Contract?.market?.displayLocale); }
  connectedCallback() {
    if (this.closest('.shopify-section')?.hasAttribute('data-pogi-buy-box-fallback')) {
      this.setAttribute('inert', '');
      this.setAttribute('aria-busy', 'false');
      delete this.dataset.pogiV2Owner;
      return;
    }
    const reconnectState = readPogiBuyBoxNativeState(this);
    this.v2Events?.abort();
    this.v2QuantityObserver?.disconnect();
    this.v2Events = new AbortController();
    const dataEl = this.querySelector('[data-pogi-data]');
    if (!dataEl) return;
    try { this.data = JSON.parse(dataEl.textContent); } catch (e) { return; }
    this.I = this.data.i18n || {};
    this.t = (k, fb, vars) => pogiTpl(this.I, k, fb, vars);
    bindPdpUsage(this);

    this.formatPos = parseInt(this.dataset.formatPos, 10) || 0;
    this.tilePos = parseInt(this.dataset.tilePos, 10) || 0;
    this.pillsPos = parseInt(this.dataset.pillsPos, 10) || 1;
    this.tileKind = this.dataset.tileKind || 'scent';
    this.unitNoun = this.dataset.unitNoun || 'bag';
    const shipThreshold = parseInt(this.dataset.shipThreshold, 10);
    this.shipThreshold = Number.isFinite(shipThreshold) ? shipThreshold : 0;
    this.moneyFormat = this.data.moneyFormat || '${{amount}}';
    this.currency = String(this.data.currency || 'USD').toUpperCase(); // presentment ISO for market-aware formatting
    this.section = this.closest('.shopify-section') || document;
    this.nativePicker = this.section.querySelector('variant-picker');
    this.nativeForm = document.getElementById(this.dataset.formId);

    this.v2Mode = ['observe', 'active'].includes(this.dataset.v2Mode) ? this.dataset.v2Mode : 'off';
    this.v2Contract = null;
    this.v2Model = null;
    this.v2Adapter = null;
    if (this.v2Mode !== 'off') {
      try {
        this.v2Contract = readPogiBuyBoxContract(this);
        this.v2Model = new PogiBuyBoxModel(this.v2Contract);
        if (this.v2Mode === 'active') {
          this.v2Adapter = new PogiBuyBoxHorizonAdapter(this, this.v2Model);
          this.v2Adapter.claim();
          this.dataset.pogiV2Owner = 'true';
        }
      } catch (error) {
        this.dataset.pogiV2Observe = 'invalid';
        console.error('[pogi-buy-box-v2] Bags contract/ownership initialization failed:', error);
        if (this.v2Mode === 'active') {
          this.#failV2(error);
          return;
        }
      }
    }

    try {
      const selectedId = reconnectState?.variantId || this.data.selectedId;
      const sel = this.data.variants.find((v) => String(v.id) === String(selectedId)) || this.data.variants[0];
      // Honor Shopify's variant and option_values deep links; otherwise default to the hero
      // format + its "Most popular" size — the prototype's choice-overload default.
      let deepLink = false;
      try {
        const params = new URLSearchParams(location.search);
        deepLink = Boolean(reconnectState) || params.has('variant') || params.has('option_values');
      } catch (e) { /* no location */ }

      this.formats = this.formatPos ? this.#uniq(this.data.variants.map((v) => v.options[this.formatPos - 1])) : [];
      this.format = this.formatPos ? ((deepLink && sel) ? sel.options[this.formatPos - 1] : this.formats[0]) : null;
      this.tile = this.tilePos ? (sel ? sel.options[this.tilePos - 1] : null) : null;

      this.intervalCount = parseInt(this.dataset.intervalCount, 10) || 3;
      this.subscribe = false; // one-time unless an exact initial plan or native reconnect state is valid
      this.intervalPlans = [];

      // Size pills are filtered to the current format; default to its "Most popular".
      this.pills = this.#sizesFor(this.format);
      this.pill = (deepLink && sel) ? sel.options[this.pillsPos - 1] : this.#defaultSize(this.format, null);
      const initialPlanId = reconnectState
        ? reconnectState.sellingPlanId
        : resolveInitialSellingPlanId(this.v2Model, this.currentVariant()?.id, globalThis.location?.search, true);
      this.pogiExplicitInterval = Boolean(initialPlanId);
      this.planId = (this.data.sellingPlan?.plans || []).find(
        (plan) => String(plan.id) === String(initialPlanId || ''),
      )?.id ?? null;
      this.tiles = this.tilePos ? this.#uniq(this.data.variants.map((v) => v.options[this.tilePos - 1])) : [];
      this._planSig = null;
      this.#applyVariantPlans();
      this.subscribe = Boolean(
        initialPlanId
        && String(this.plan()?.id ?? '') === initialPlanId
        && this.#allocationFor(this.currentVariant(), this.plan()),
      );
      const selectedV2Variant = this.v2Model?.resolveVariant(this.#v2Selections());
      if (reconnectState && this.v2Mode === 'active') {
        this.v2Adapter.updateCurrentCartQuantityFromForm();
      }
      this.qty = this.v2Mode === 'active' && selectedV2Variant
        ? this.v2Adapter.quantityState(
          selectedV2Variant.id,
          reconnectState?.quantity ?? selectedV2Variant.quantityRule.min,
        ).value
        : reconnectState?.quantity ?? 1;

      this.#renderFormats();
      this.#renderPills();
      this.#renderTiles();
      this.#renderFrequencies();
      this.#ensureSellingPlanInput();
      this.#bind();
      this.section.addEventListener(StandardEvents.productSelect, (event) => {
        event.promise?.then(() => {
          this.update();
        }).catch((error) => {
          if (error?.name === 'AbortError') return;
          if (this.v2Mode === 'active') this.#failV2(error);
        });
      }, { signal: this.v2Events.signal });
      if (this.v2Mode === 'active') {
        document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
          event.promise?.then(({ detail }) => {
            if (!this.isConnected || !this.v2Adapter?.updateCartItems(detail?.items)) return;
            this.update();
          }).catch((error) => {
            if (error?.name !== 'AbortError') {
              console.warn('[pogi-buy-box-v2] Bags cart quantity refresh failed:', error);
            }
          });
        }, { signal: this.v2Events.signal });
        this.v2QuantityObserver = new MutationObserver((records) => {
          const changedInput = records.find((record) =>
            record.type === 'attributes'
            && record.attributeName === 'data-cart-quantity'
            && record.target instanceof HTMLInputElement)?.target;
          if (
            this.v2Mode !== 'active'
            || !(changedInput instanceof HTMLInputElement)
            || !this.v2Adapter?.updateCurrentCartQuantityFromForm(changedInput)
          ) return;
          this.update();
        });
        this.v2QuantityObserver.observe(this.v2Adapter.form, {
          subtree: true,
          childList: true,
          attributes: true,
          attributeFilter: ['data-cart-quantity'],
        });
      }
      this.#driveNative(); // reflect the default (30/Fresh) on the native picker → correct ATC on load
      this.update();
      this.#injectGalleryBadge();
      this.#bindPressModal();
      this.#fixPreviewLinks('compostable');
      this.querySelectorAll('[data-pogi-skeleton]').forEach((node) => node.remove());
      this.setAttribute('data-ready', '');
      this.removeAttribute('inert');
      this.setAttribute('aria-busy', 'false');
      this.dispatchEvent(new CustomEvent('pogi:buybox-ready', { bubbles: true }));
    } catch (error) {
      if (this.v2Mode === 'active') {
        console.error('[pogi-buy-box-v2] Bags active initialization failed:', error);
        this.#failV2(error);
        return;
      }
      throw error;
    }
  }

  disconnectedCallback() {
    this.v2Events?.abort();
    this.v2QuantityObserver?.disconnect();
    this.v2Adapter?.release();
  }

  #failV2(error) {
    this.v2Events?.abort();
    this.v2QuantityObserver?.disconnect();
    this.v2Mode = 'failed';
    if (this.v2Adapter) this.v2Adapter.revealNativeControls(error);
    else revealPogiBuyBoxNativeControls(this, error);
    delete this.dataset.pogiV2Owner;
  }

  // In preview (?preview_theme_id / ?view present), carry the preview context onto the
  // material-toggle link so bags⇄compostable navigation stays on the bespoke templates
  // (the bare URL falls back to the default/wipes template until the suffix is set at
  // launch). No-op on the live storefront — no preview params → links stay clean.
  #fixPreviewLinks(view) {
    let params;
    try { params = new URLSearchParams(location.search); } catch (e) { return; }
    const pt = params.get('preview_theme_id');
    if (!pt && !params.get('view')) return;
    this.querySelectorAll('a.pogi-bb__mat[href]').forEach((a) => {
      try {
        const u = new URL(a.getAttribute('href'), location.origin);
        u.searchParams.set('view', view);
        if (pt) u.searchParams.set('preview_theme_id', pt);
        a.setAttribute('href', u.pathname + u.search);
      } catch (e) { /* leave as-is */ }
    });
  }

  // §13.4 — the mobile press card opens a native <dialog>. Backdrop click + the
  // close button both dismiss it. No-op when the card/dialog aren't rendered.
  #bindPressModal() {
    const openBtn = this.querySelector('[data-pogi-press-open]');
    const modal = this.querySelector('[data-pogi-press-modal]');
    if (!openBtn || !modal || typeof modal.showModal !== 'function') return;
    openBtn.addEventListener('click', () => modal.showModal());
    modal.querySelector('[data-pogi-press-close]')?.addEventListener('click', () => modal.close());
    modal.addEventListener('click', (e) => { if (e.target === modal) modal.close(); });
  }

  // §26.1 — pin a BESTSELLER chip top-left over the hero gallery tile.
  #injectGalleryBadge() {
    const text = (this.dataset.galleryBadge || '').trim();
    if (!text) return;
    const tile = this.section.querySelector('.product-media-container');
    if (!tile || tile.querySelector('.pogi-gallery-badge')) return;
    const chip = document.createElement('span');
    chip.className = 'pogi-gallery-badge';
    chip.textContent = text;
    tile.appendChild(chip);
  }

  #uniq(a) { return a.filter((x, i) => x != null && a.indexOf(x) === i); }
  #esc(s) { return String(s).replace(/"/g, '&quot;').replace(/</g, '&lt;'); }
  #slug(s) { return String(s).toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, ''); }

  money(cents) {
    const value = Number(cents);
    if (!Number.isFinite(value)) return '';
    // Match the Liquid `money` output beside JS-rendered prices and totals.
    if (this.data?.moneyFormat) return formatShopifyMoneyWithTemplate(value, this.data.moneyFormat, this.currency || 'USD');
    return formatShopifyMoney(value, String(this.moneyFormat || '{{amount}}'), this.currency || 'USD');
  }
  moneyWhole(cents) {
    return formatShopifyWholeMoney(cents, this.moneyFormat, this.currency || 'USD');
  }
  unitMoney(cents, count) {
    return formatShopifyUnitMoney(cents, count, this.currency || 'USD');
  }
  showDerived() { return this.currency === 'USD'; }
  // Primary unit-price display comes from Shopify's presentment values. Liquid's
  // formatted string is preferred for exact server/client round-trip parity.
  priceMoney(v) { return (v && v.money) || this.money(v ? v.price : 0); }
  cardPrice(v, plan) {
    return resolveShopifyPdpPrice(v, this.subscribe ? this.#allocationFor(v, plan) : null, this.subscribe);
  }
  cardMoney(v, price) {
    if (!price) return '';
    return !this.showDerived() && price.money
      ? price.money
      : this.money(price.amount);
  }
  #allocationFor(variant, plan) {
    if (!variant || !plan || !Array.isArray(variant.allocations)) return null;
    if (this.v2Mode === 'active' && this.v2Model && !this.v2Model.allocationFor(variant.id, plan.id)) {
      return null;
    }
    return variant.allocations.find((allocation) =>
      String(allocation.planId) === String(plan.id) &&
      Number.isSafeInteger(allocation.price) &&
      allocation.price >= 0) || null;
  }
  parseCount(v) {
    if (!v) return 0;
    const m = String(v).match(/(\d[\d,]*)\s*-?\s*count/i);
    if (m) return parseInt(m[1].replace(/,/g, ''), 10);
    const n = String(v).match(/\d[\d,]*/);
    return n ? parseInt(n[0].replace(/,/g, ''), 10) : 0;
  }
  // Unit count for economics and the sizing calculator. Translation order is
  // not stable: Portuguese can put bags before rolls ("270 sacos · 18 rolos").
  // Across the current catalog, bag count is always the largest exact numeric
  // token; this also covers bare-count Jumbo and Tie-Handle options.
  unitCount(v) {
    const values = (String(v).match(/[0-9][0-9.,\s\u00a0\u202f]*/g) || [])
      .map((raw) => parseInt(raw.replace(/[^0-9]/g, ''), 10))
      .filter((count) => Number.isFinite(count) && count > 0);
    return values.length ? Math.max(...values) : 0;
  }
  // Headline = the text before the structural separator ("10 Rolls (150 Bags)" →
  // "10 Rolls"; "10 rollen · 150 zakjes" → "10 rollen"; "300 Bags" → "300 Bags").
  headline(v) { return String(v).split(/[(（·]/)[0].trim() || String(v); }
  // Text after the separator, shown as the pill subtitle ("150 Bags"); '' when none.
  subLabel(v) {
    const s = String(v);
    const m = s.match(/[(（]([^)）]+)[)）]/);
    if (m) return m[1].trim();
    const i = s.indexOf('·');
    return i >= 0 ? s.slice(i + 1).trim() : '';
  }
  estMonths(bags) { return pdpUsageDuration(bags, this.getDailyUsage(), 'months'); }
  // Bottom-of-card benefit caption per role. EN carries the personality; ja overrides
  // (js_bagcap_*) keep it literal — the jokes don't translate. '' = fall back to bag count.
  bagCaption(roleK) {
    const F = { onthego: 'Starter pack', popular: 'What most paws grab', bundle: '2 dispensers, on us', everyday: 'Your daily driver', value: 'Lowest cost per scoop' };
    return roleK && F[roleK] ? this.t('js_bagcap_' + roleK, F[roleK]) : '';
  }

  /* ---------- format axis ---------- */
  // Slug of the EN value ("Refill Rolls" → 'refill-rolls'). Translated values (ja
  // 詰め替えロール) slug to '' — fall back to CATALOG ORDER, which is identical in
  // every locale, so glyphs/roles/jumbo detection survive translation.
  #formatKey(name) {
    const s = this.#slug(name);
    if (ROLE[s] || FORMAT_GLYPH[s]) return s;
    const i = this.formats ? this.formats.indexOf(name) : -1;
    return ['refill-rolls', 'jumbo-roll', 'tie-handle'][i] || s;
  }
  #isJumbo(name) { return this.#formatKey(name) === 'jumbo-roll'; }
  #roleFor(format, count) { return (ROLE[this.#formatKey(format)] || {})[count] || { t: '' }; }
  // Unique Size values among variants of a given format, in catalog order.
  #sizesFor(format) {
    let sizes;
    if (!this.formatPos) sizes = this.#uniq(this.data.variants.map((v) => v.options[this.pillsPos - 1]));
    else sizes = this.#uniq(this.data.variants.filter((v) => v.options[this.formatPos - 1] === format).map((v) => v.options[this.pillsPos - 1]));
    // Sort the ladder by count ASCENDING so its display order (10/30/50/60) is
    // independent of the catalog's variant/option order. This lets the merchant make
    // the 30-pack the product's DEFAULT (first) variant — for a flash-free load —
    // without the visible size ladder reshuffling.
    return sizes.sort((a, b) => (this.parseCount(a) || 0) - (this.parseCount(b) || 0));
  }
  // Default Size for a format: the "Most popular" (accent) one, else the prior pick if
  // still valid, else the first.
  #defaultSize(format, prefer) {
    const sizes = this.#sizesFor(format);
    if (prefer && sizes.includes(prefer)) return prefer;
    const popular = sizes.find((s) => { const r = this.#roleFor(format, this.parseCount(s)); return r.k === 'popular'; });
    return popular || sizes[0];
  }
  // Lowest price-per-bag in the CURRENT format (the SAVE anchor).
  refPerUnit(plan) {
    let reference = null;
    this.pills.forEach((s) => {
      const v = this.variantObj(s);
      const count = this.unitCount(s);
      const price = this.cardPrice(v, plan);
      if (price && count && (!reference || (price.amount / count) > (reference.amount / reference.count))) {
        reference = { amount: price.amount, count };
      }
    });
    return reference;
  }
  variantObj(pillVal, format = this.format, tile = this.tile) {
    const legacyVariant = this.data.variants.find((v) =>
      v.options[this.pillsPos - 1] === pillVal &&
      (this.tilePos === 0 || v.options[this.tilePos - 1] === tile) &&
      (this.formatPos === 0 || v.options[this.formatPos - 1] === format));
    return this.#v2CommerceVariant(legacyVariant);
  }

  #v2CommerceVariant(legacyVariant) {
    if (this.v2Mode !== 'active' || !this.v2Model || !legacyVariant) return legacyVariant || null;
    const normalized = this.v2Model.variantById(legacyVariant.id);
    if (!normalized) return null;
    return {
      ...legacyVariant,
      id: normalized.id,
      available: normalized.available,
      price: normalized.price.shopifyAmount,
      money: normalized.price.formatted,
      options: [...this.v2Contract.axes]
        .sort((left, right) => left.optionPosition - right.optionPosition)
        .map((axis) => normalized.selections[axis.key]),
      allocations: normalized.allocations.map((allocation) => ({
        planId: allocation.planId,
        price: allocation.price.shopifyAmount,
        money: allocation.price.formatted,
        savings: allocation.savings.shopifyAmount,
        savingsMoney: allocation.savings.formatted,
      })),
      mediaId: normalized.mediaId,
      sub: normalized.subscriptionConfig,
    };
  }

  #v2Selections() {
    const selections = {};
    for (const axis of this.v2Contract?.axes || []) {
      if (axis.key === 'format') selections[axis.key] = this.format;
      else if (axis.key === 'count') selections[axis.key] = this.pill;
      else if (axis.key === this.tileKind) selections[axis.key] = this.tile;
    }
    return selections;
  }

  currentVariant() {
    if (this.v2Mode === 'active' && this.v2Model) {
      const normalized = this.v2Model.resolveVariant(this.#v2Selections());
      const legacyVariant = normalized
        ? this.data.variants.find((variant) => String(variant.id) === normalized.id)
        : null;
      return this.#v2CommerceVariant(legacyVariant);
    }
    return this.variantObj(this.pill);
  }

  #recordV2Observation() {
    if (this.v2Mode !== 'observe' || !this.v2Model) return;
    const legacyVariant = this.variantObj(this.pill);
    const normalizedVariant = this.v2Model.resolveVariant(this.#v2Selections());
    const mismatches = [];
    if (String(legacyVariant?.id ?? '') !== String(normalizedVariant?.id ?? '')) mismatches.push('variant');
    if (legacyVariant && normalizedVariant) {
      if (Boolean(legacyVariant.available) !== normalizedVariant.available) mismatches.push('availability');
      if (Number(legacyVariant.price) !== normalizedVariant.price.shopifyAmount) mismatches.push('price');
      const legacyAllocations = (legacyVariant.allocations || [])
        .map((allocation) => {
          const savings = Number.isSafeInteger(allocation.savings)
            ? allocation.savings
            : Number(legacyVariant.price) - Number(allocation.price);
          return `${allocation.planId}:${allocation.price}:${savings}`;
        })
        .sort();
      const normalizedAllocations = normalizedVariant.allocations
        .map((allocation) => `${allocation.planId}:${allocation.price.shopifyAmount}:${allocation.savings.shopifyAmount}`)
        .sort();
      if (legacyAllocations.join('|') !== normalizedAllocations.join('|')) mismatches.push('allocations');

      const configuredPlans = resolveConfiguredPlanSet(this.v2Model, normalizedVariant.id).plans
        .map((plan) => plan.id)
        .sort();
      const legacyPlans = (this.intervalPlans || []).map((plan) => String(plan.id)).sort();
      if (configuredPlans.join('|') !== legacyPlans.join('|')) mismatches.push('plans');

      const quantityInput = this.nativeForm?.querySelector('input[name="quantity"]');
      const quantityOwner = quantityInput?.closest('quantity-selector-component');
      if (
        quantityInput instanceof HTMLInputElement
        && quantityOwner instanceof HTMLElement
        && quantityOwner.dataset.variantId === normalizedVariant.id
      ) {
        const rule = normalizedVariant.quantityRule;
        const nativeRule = {
          min: Number(quantityInput.min || 1),
          max: quantityInput.max ? Number(quantityInput.max) : null,
          step: Number(quantityInput.step || 1),
          cartQuantity: Number(quantityInput.dataset.cartQuantity || 0),
        };
        if (JSON.stringify(rule) !== JSON.stringify(nativeRule)) mismatches.push('quantity');
      }
    }
    this.dataset.pogiV2Observe = mismatches.length ? 'mismatch' : 'match';
    if (mismatches.length) {
      console.warn('[pogi-buy-box-v2] Bags observation mismatch:', {
        mismatches,
        legacyVariantId: legacyVariant?.id ?? null,
        normalizedVariantId: normalizedVariant?.id ?? null,
      });
    }
  }

  #fromPrice(format) {
    const prices = this.data.variants.filter((v) => !this.formatPos || v.options[this.formatPos - 1] === format).map((v) => v.price);
    return prices.length ? Math.min.apply(null, prices) : 0;
  }
  // "from [[price]]" uses the cheapest Shopify variant in the format and its
  // authoritative presentment string.
  #fromMoney(format) {
    const inFmt = this.data.variants.filter((v) => !this.formatPos || v.options[this.formatPos - 1] === format);
    if (!inFmt.length) return this.money(0);
    const min = inFmt.reduce((a, b) => (b.price < a.price ? b : a));
    return this.priceMoney(min);
  }

  /* ---------- rendering ---------- */
  #renderFormats() {
    const wrap = this.querySelector('[data-pogi-formats]');
    if (!wrap || !this.formats.length) return;
    wrap.innerHTML = this.formats.map((f) => {
      const key = this.#formatKey(f);
      const glyph = FORMAT_GLYPH[key] || '';
      return `
      <button type="button" class="pogi-bb__format" role="radio" data-pogi-format="${this.#esc(key)}" aria-checked="false">
        ${glyph ? `<span class="pogi-bb__format-glyph" aria-hidden="true">${glyph}</span>` : ''}
        <span class="pogi-bb__format-name">${this.#esc(f)}</span>
        <span class="pogi-bb__format-desc">${this.#esc(this.t('js_fmt_desc_' + ({ 'refill-rolls': 'refill', 'jumbo-roll': 'jumbo', 'tie-handle': 'tie' }[key] || key), FORMAT_DESC[key] || ''))}</span>
        <span class="pogi-bb__format-from">${this.t('js_from', 'from [[price]]', { price: this.#fromMoney(f) })}</span>
      </button>`;
    }).join('');
  }

  #renderPills() {
    const wrap = this.querySelector('[data-pogi-sizes]');
    if (!wrap) return;
    const singleton = this.pills.length === 1;
    wrap.setAttribute('data-cols', String(this.pills.length === 3 ? 3 : (singleton ? 1 : 2)));
    wrap.setAttribute('role', singleton ? 'group' : 'radiogroup');
    wrap.innerHTML = singleton
      ? `<div class="pogi-bb__single-size"><strong>${this.#esc(this.headline(this.pills[0]))}</strong><span data-pogi-single-unit-price></span></div>`
      : `<div class="pogi-bb__pills">${this.pills.map((s) => this.#pill(s)).join('')}</div>`;
  }

  #pill(v) {
    const role = this.#roleFor(this.format, this.parseCount(v));
    return `<button type="button" class="pogi-bb__pill" data-role-key="${role.k || ''}" role="radio" data-pogi-size="${this.#esc(v)}" data-pogi-bags="${this.unitCount(v)}" aria-checked="false">
      <span class="pogi-bb__pill-top"><span class="pogi-bb__pill-role${role.accent ? ' pogi-bb__pill-role--accent' : ''}">${this.#esc(role.k ? this.t('js_role_' + role.k, role.t) : role.t)}</span><span class="pogi-bb__pill-check" aria-hidden="true">✓</span></span>
      <span class="pogi-bb__pill-mid"><span class="pogi-bb__pill-size">${this.#esc(this.headline(v))}</span><span class="pogi-bb__pill-price" data-pill-price></span></span>
      <span class="pogi-bb__pill-bot"><span class="pogi-bb__pill-per" data-pill-per></span><span class="pogi-bb__pill-save" data-pill-save></span></span>
      <span class="pogi-bb__pill-supply" data-pill-supply></span>
    </button>`;
  }

  #renderTiles() {
    const wrap = this.querySelector('[data-pogi-tiles]');
    if (!wrap || !this.tiles.length) return;
    wrap.innerHTML = this.tiles.map((s) => {
      const ff = /^\s*(?:fragrance-free|unscented|無香料?|sans parfum|ohne (?:parf(?:ü|ü)m|duftstoffe)|sin perfume|senza profumo|zonder (?:parfum|geur)|bezzapach[a-ząćęłńóśźż]*|sem perfume)\s*$/i.test(s); // exact-value ff detection, all 9 locales (NFC+NFD de); full phrases only — generic scent words (parfum/duft/geur/perfume) must NOT match
      const icon = ff ? ICONS.droplet : ICONS.sprout;
      const accent = ff ? 'var(--pogi-olive-deep)' : 'var(--pogi-olive)';
      const l1 = ff ? this.t('js_scent_ff_1', 'No fragrance added') : this.t('js_bags_fresh_1', 'Light, clean finish at pickup');
      const l2 = ff ? this.t('js_bags_ff_2', 'Best for sensitive noses') : this.t('js_bags_fresh_2', 'Fresh · fades fast');
      return `<button type="button" class="pogi-bb__scent" role="radio" data-pogi-tile="${this.#esc(s)}" aria-checked="false">
        <span class="pogi-bb__scent-icon" style="background:${accent}">${icon}</span>
        <span class="pogi-bb__scent-text"><span class="pogi-bb__scent-name">${this.#esc(s)}</span><span class="pogi-bb__scent-l1">${l1}</span><span class="pogi-bb__scent-l2">${l2}</span></span>
      </button>`;
    }).join('');
  }

  #freqWeeks(name) {
    const pi = pogiParseFreq(name);
    if (!pi) return null;
    if (pi.u === 'month') return Math.round((pi.n * 30) / 7);
    if (pi.u === 'week') return pi.n;
    return Math.round(pi.n / 7);
  }
  #parseInterval(name) {
    return pogiParseFreq(name); // shared locale-proof parser (see POGI_FREQ_WORDS above)
  }
  // Per-variant interval config injected as variant.sub ("unit|opts|default").
  #cfgFor(variant) {
    const raw = variant && variant.sub;
    if (!raw) return null;
    const parts = String(raw).split('|');
    if (parts.length < 2 || !parts[1]) return null;
    const options = [...new Set(parts[1].split(',').map(Number).filter(Number.isFinite))];
    return { u: parts[0], o: options, d: Number(parts[2]) };
  }
  // Recharge plans are product-level; show only the selected variant's configured
  // intervals (matched to live plans by parsed interval), default to its mid option.
  #applyVariantPlans() {
    const all = (this.data.sellingPlan && this.data.sellingPlan.plans) || [];
    const variant = this.currentVariant();
    if (this.v2Mode === 'active' && this.v2Model && variant) {
      const resolved = resolveConfiguredPlanSet(this.v2Model, variant.id, this.pogiExplicitInterval ? this.planId : null);
      const plans = resolved.plans
        .map((plan) => all.find((legacyPlan) => String(legacyPlan.id) === plan.id))
        .filter(Boolean);
      this.intervalPlans = plans;
      const sig = plans.map((plan) => plan.id).join(',');
      const selected = plans.find((plan) => String(plan.id) === resolved.selectedPlanId) || null;
      if (sig !== this._planSig) {
        this._planSig = sig;
        this.planId = selected?.id ?? plans[0]?.id ?? null;
        this.#renderFrequencies();
      } else if (!this.pogiExplicitInterval || !plans.some((plan) => String(plan.id) === String(this.planId))) {
        this.planId = selected?.id ?? plans[0]?.id ?? null;
      }
      return;
    }
    const cfg = this.#cfgFor(variant);
    let plans;
    if (cfg) {
      plans = cfg.o
        .map((c) => all.find((p) => {
          const pi = this.#parseInterval(p.name);
          return pi && pi.u === cfg.u && pi.n === c && this.#allocationFor(variant, p);
        }))
        .filter(Boolean);
    } else {
      // Product-level plans are not safe defaults for an unconfigured variant.
      // Preserve one-time purchase while failing subscription closed.
      plans = [];
    }
    this.intervalPlans = plans;
    const sig = plans.map((p) => p.id).join(',');
    if (sig !== this._planSig) {
      this._planSig = sig;
      const def = cfg ? plans.find((p) => { const pi = this.#parseInterval(p.name); return pi && pi.n === cfg.d; }) : null;
      this.planId = (def && def.id) || (plans[0] && plans[0].id) || null;
      this.#renderFrequencies();
    } else if (!plans.some((p) => p.id === this.planId)) {
      this.planId = (plans[0] && plans[0].id) || null;
    }
  }
  #renderFrequencies() {
    const wrap = this.querySelector('[data-pogi-freq-opts]');
    if (!wrap) return;
    if (!this.intervalPlans || !this.intervalPlans.length) {
      wrap.innerHTML = '';
      return;
    }
    const intervalUnit = (u, n) => {
      const plural = n > 1;
      if (u === 'month') return this.t(plural ? 'js_unit_months' : 'js_unit_month', plural ? 'months' : 'month');
      if (u === 'day') return this.t(plural ? 'js_unit_days' : 'js_unit_day', plural ? 'days' : 'day');
      return this.t(plural ? 'js_unit_weeks' : 'js_unit_week', plural ? 'weeks' : 'week');
    };
    const options = this.intervalPlans.map((p) => {
      const pi = this.#parseInterval(p.name);
      const label = pi ? this.t('js_freq', '[[dur]]', { dur: this.t('js_dur', '[[n]] [[unit]]', { n: pi.n, unit: intervalUnit(pi.u, pi.n) }) }) : String(p.name);
      return { value: p.id, label, days: pi ? pi.n * ({ day: 1, week: 7, month: 30 }[pi.u] || 0) : null, months: pi?.u === 'month' };
    });
    renderPdpIntervalSelect(this, options, this.planId, (value) => {
      this._starterSelectionRevision = (this._starterSelectionRevision || 0) + 1;
      this.pogiExplicitInterval = true;
      this.planId = value;
      this.update();
    });
  }

  /* ---------- events ---------- */
  #bind() {
    this.addEventListener('click', (e) => {
      const fmt = e.target.closest('[data-pogi-format]');
      const pill = e.target.closest('[data-pogi-size]');
      const tile = e.target.closest('[data-pogi-tile]');
      const mode = e.target.closest('[data-pogi-mode]');
      const freq = e.target.closest('[data-pogi-freq]');
      const dt = e.target.closest('[data-pogi-desc-toggle]');
      const dec = e.target.closest('[data-pogi-qty-dec]');
      const inc = e.target.closest('[data-pogi-qty-inc]');
      if (fmt) { this.#setFormat(fmt.dataset.pogiFormat); }
      else if (pill) { this.pill = pill.dataset.pogiSize; this.#commitSelection(); }
      else if (tile) { this.tile = tile.dataset.pogiTile; this.#commitSelection(); }
      else if (mode) { this.subscribe = mode.dataset.pogiMode === 'subscribe'; this.update(); }
      else if (freq) { this.pogiExplicitInterval = true; this.planId = parseInt(freq.dataset.pogiFreq, 10); this.update(); }
      else if (dt) { const d = this.querySelector('[data-pogi-desc]'); const o = d.classList.toggle('is-open'); dt.textContent = o ? this.t('read_less', 'Read less') : this.t('read_more', 'Read more'); dt.setAttribute('aria-expanded', String(o)); }
      else if (dec) { this.#changeQty(-1); }
      else if (inc) { this.#changeQty(1); }
    }, { signal: this.v2Events.signal });
    // A11Y (audit P3, 2026-07-23): ONE delegated keydown per component root —
    // roving-tabindex arrow-key support for the radio groups (see #roveKey).
    this.addEventListener('keydown', (e) => this.#roveKey(e), { signal: this.v2Events.signal });
  }

  #commitSelection() {
    try {
      this.#driveNative();
      this.update();
    } catch (error) {
      if (this.v2Mode !== 'active') throw error;
      console.error('[pogi-buy-box-v2] Bags selection failed closed:', error);
      this.#failV2(error);
    }
  }

  /* ---- a11y: roving tabindex (audit P3 + countersign #19, 2026-07-23) ---- */
  // ONE tab stop per radio group: tabindex 0 on the checked ELIGIBLE radio
  // (first eligible if none), -1 on every other radio — hidden/disabled ones
  // included, so they can never hold a stale tab stop. Called at the END of
  // update(), AFTER the pill display-sync and [hidden] toggles, so eligibility
  // is judged on the pass's final visibility state. Behavior-additive: click
  // handling is untouched.
  #rove() {
    this.querySelectorAll(POGI_ROVE_GROUPS).forEach((g) => {
      const all = [...g.querySelectorAll('[role="radio"]')];
      const radios = all.filter(POGI_ROVE_ELIGIBLE);
      all.forEach((b) => b.setAttribute('tabindex', '-1')); // hidden/disabled are never the tab stop
      if (!radios.length) return;
      const on = radios.find((b) => b.getAttribute('aria-checked') === 'true') || radios[0];
      on.setAttribute('tabindex', '0'); // checked-and-eligible, else first ELIGIBLE
    });
  }
  // Arrow keys move focus AND activate the next/prev radio with wrap-around;
  // Home/End jump to first/last. Activation goes through .click() — the exact
  // path pointer input takes — so selection, native-picker drive and update()
  // all reuse the existing delegated click handler above.
  #roveKey(e) {
    if (!['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'].includes(e.key)) return;
    const radio = e.target.closest('[role="radio"]');
    const group = radio && radio.closest(POGI_ROVE_GROUPS);
    if (!group) return; // not one of our groups (e.g. the sizing modal) — leave the key alone
    const radios = [...group.querySelectorAll('[role="radio"]')].filter(POGI_ROVE_ELIGIBLE); // arrows skip hidden/disabled radios
    const i = radios.indexOf(radio);
    if (i < 0) return;
    e.preventDefault();
    const next = e.key === 'Home' ? radios[0]
      : e.key === 'End' ? radios[radios.length - 1]
        : (e.key === 'ArrowRight' || e.key === 'ArrowDown') ? radios[(i + 1) % radios.length]
          : radios[(i - 1 + radios.length) % radios.length];
    if (next === radio) return;
    next.focus();
    next.click();
    // A click can rebuild the clicked group's innerHTML (e.g. compostable freqs
    // re-render on selection) — re-focus the fresh tab stop so keyboard context
    // is never dropped mid-interaction.
    if (!next.isConnected) { const nb = group.querySelector('[role="radio"][tabindex="0"]'); if (nb) nb.focus(); }
  }
  // Changing format resets to that format's default size, re-renders the ladder.
  #setFormat(key) {
    const f = this.formats.find((x) => this.#formatKey(x) === key);
    if (f == null) return;
    this.format = f;
    this.pills = this.#sizesFor(f);
    this.pill = this.#defaultSize(f, this.pill);
    this.#renderPills();
    this.#commitSelection();
  }
  #setNativeOption(fieldsetIndex, value) {
    if (!this.nativePicker || fieldsetIndex < 0) return;
    const inputs = this.nativePicker.querySelectorAll(`input[data-fieldset-index="${fieldsetIndex}"]`);
    for (const input of inputs) {
      if (input.value === value && !input.checked) { input.checked = true; input.dispatchEvent(new Event('input', { bubbles: true })); input.dispatchEvent(new Event('change', { bubbles: true })); return; }
    }
  }
  #driveNative() {
    if (this.v2Mode === 'active' && this.v2Adapter && this.v2Model) {
      const variant = this.currentVariant();
      if (!variant) throw new Error('The selected Bags options do not resolve to one variant');
      this.v2Adapter.selectVariant(variant.id);
      return;
    }
    if (this.formatPos && this.format != null) this.#setNativeOption(this.formatPos - 1, this.format);
    if (this.tile != null) this.#setNativeOption(this.tilePos - 1, this.tile);
    if (this.pill != null) this.#setNativeOption(this.pillsPos - 1, this.pill);
  }

  plan() {
    return (this.intervalPlans || []).find((p) => p.id === this.planId) || null;
  }

  update() {
    this.#applyVariantPlans();
    const variant = this.currentVariant();
    if (this.v2Mode === 'active' && !variant) {
      this.#failV2(new Error('The selected Bags options do not resolve to one variant'));
      return;
    }
    const selectedPlan = this.plan();
    const allocation = this.#allocationFor(variant, selectedPlan);
    const subscriptionValid = Boolean(selectedPlan && allocation);
    const ref = this.refPerUnit(selectedPlan);
    const jumbo = this.#isJumbo(this.format);
    const quantityState = this.v2Mode === 'active' && this.v2Model && variant
      ? this.v2Adapter.quantityState(variant.id, this.qty)
      : null;
    if (quantityState) {
      this.qty = quantityState.value;
      const quantityOutput = this.querySelector('[data-pogi-qty]');
      if (quantityOutput) quantityOutput.textContent = this.qty;
      const decrement = this.querySelector('[data-pogi-qty-dec]');
      const increment = this.querySelector('[data-pogi-qty-inc]');
      const activeStepper = [decrement, increment].find((button) => button && button === document.activeElement);
      if (decrement) decrement.disabled = this.qty <= quantityState.min;
      if (increment) {
        increment.disabled = !quantityState.canAdd
          || (quantityState.remaining !== null && this.qty + quantityState.step > quantityState.remaining);
      }
      // A focused stepper that disables itself would drop keyboard focus to the
      // page; move focus to the other stepper while that one is still usable.
      if (activeStepper?.disabled) [decrement, increment].find((button) => button && button !== activeStepper && !button.disabled)?.focus();
    }

    this.querySelectorAll('[data-pogi-format]').forEach((b) => b.setAttribute('aria-checked', String(b.dataset.pogiFormat === this.#formatKey(this.format))));
    this.querySelectorAll('[data-pogi-size]').forEach((b) => b.setAttribute('aria-checked', String(b.dataset.pogiSize === this.pill)));
    this.querySelectorAll('[data-pogi-tile]').forEach((b) => b.setAttribute('aria-checked', String(b.dataset.pogiTile === this.tile)));
    this.querySelectorAll('[data-pogi-mode]').forEach((b) => b.setAttribute('aria-checked', String((b.dataset.pogiMode === 'subscribe') === this.subscribe)));
    const subscribeMode = this.querySelector('[data-pogi-mode="subscribe"]');
    if (subscribeMode) {
      subscribeMode.disabled = !subscriptionValid;
      subscribeMode.setAttribute('aria-disabled', String(!subscriptionValid));
    }
    const intervalSelect = this.querySelector('[data-pogi-interval-select]');
    if (intervalSelect) intervalSelect.value = String(this.planId);
    this.querySelectorAll('[data-pogi-freq]').forEach((b) => b.setAttribute('aria-checked', String(parseInt(b.dataset.pogiFreq, 10) === this.planId)));
    const formatHelp = this.querySelector('[data-pogi-format-help]');
    if (formatHelp) formatHelp.textContent = this.querySelector('[data-pogi-format][aria-checked="true"] .pogi-bb__format-desc')?.textContent || '';
    const units = this.unitCount(this.pill);
    const singlePrice = this.querySelector('[data-pogi-single-unit-price]');
    if (singlePrice) {
      const selectedPrice = this.cardPrice(variant, selectedPlan);
      singlePrice.textContent = selectedPrice && units ? pdpUsagePriceText(this, {
        price: this.showDerived() ? (selectedPrice.amount / units).toFixed(1) : this.unitMoney(selectedPrice.amount, units),
        unit: this.unitNoun, cents: this.showDerived(),
      }) : '';
    }
    const flab = this.querySelector('[data-pogi-format-label]');
    if (flab && this.format) flab.textContent = this.format;
    const tlab = this.querySelector('[data-pogi-tile-label]');
    if (tlab && this.tile) tlab.textContent = this.tile;

    this.querySelectorAll('[data-pogi-size]').forEach((btn) => {
      const pv = btn.dataset.pogiSize;
      const v = this.variantObj(pv);
      const units = this.unitCount(pv);    // economics count (bags)
      const setT = (sel, val) => pogiSetTranslatedText(btn.querySelector(sel), val);
      const price = this.cardPrice(v, selectedPlan);
      if (v && price) {
        setT('[data-pill-price]', this.cardMoney(v, price));
        if (jumbo) {
          // Jumbo Roll: descriptor instead of ¢/bag + SAVE (the headline already shows bags).
          setT('[data-pill-per]', this.t('js_jumbo_desc', JUMBO_DESC));
          setT('[data-pill-save]', '');
          setT('[data-pill-supply]', '');
        } else {
          const perLine = !units ? '' : pdpUsagePriceText(this, {
            price: this.showDerived() ? (price.amount / units).toFixed(1) : this.unitMoney(price.amount, units),
            unit: this.unitNoun, units, perDay: this.getDailyUsage(), maxUnit: 'months',
            locale: this.v2Contract?.market?.displayLocale, cents: this.showDerived(),
          });
          setT('[data-pill-per]', perLine);
          const saveCents = ref ? calculateShopifyUnitSavings(ref.amount, ref.count, price.amount, units) : 0;
          setT('[data-pill-save]', this.showDerived() && saveCents > 0 ? this.t('js_save_caps', 'SAVE [[amount]]', { amount: this.moneyWhole(saveCents) }) : '');
          const cap = this.bagCaption(this.#roleFor(this.format, this.parseCount(pv)).k);
          setT('[data-pill-supply]', units && cap
            ? this.t('js_bag_supply', '[[desc]] · [[n]] bags', { desc: cap, n: units })
            : this.subLabel(pv));
        }
        btn.style.display = '';
      } else if (v && pv === this.pill) {
        setT('[data-pill-price]', '');
        setT('[data-pill-per]', '');
        setT('[data-pill-save]', '');
        setT('[data-pill-supply]', '');
        btn.style.display = '';
      } else { btn.style.display = 'none'; }
    });

    if (variant) {
      const savings = subscriptionValid ? Math.max(0, variant.price - allocation.price) : 0;
      const setT = (sel, val) => pogiSetTranslatedText(this.querySelector(sel), val);
      setT('[data-pogi-sub-price]', subscriptionValid
        ? (allocation.money || this.money(allocation.price))
        : this.priceMoney(variant));
      setT('[data-pogi-sub-was]', subscriptionValid && savings > 0 ? this.priceMoney(variant) : '');
      setT('[data-pogi-ot-price]', this.priceMoney(variant));
      const badge = this.querySelector('[data-pogi-save-badge]');
      if (badge) badge.textContent = subscriptionValid && savings > 0
        ? (this.showDerived()
          ? this.t('js_save', 'Save [[amount]]', { amount: this.money(savings) })
          : this.t('js_save_pct', '[[pct]]% off', {
            pct: Math.round((savings / variant.price) * 100),
          }))
        : '';
    }

    const subx = this.querySelector('[data-pogi-subx]');
    if (subx) subx.hidden = !this.subscribe || !subscriptionValid;
    const subxDesc = this.querySelector('[data-pogi-subx-desc]');
    if (subxDesc) subxDesc.textContent = this.t('js_choose_interval', 'Choose a delivery interval that fits your use.');

    if (this.v2Mode === 'active' && this.v2Adapter && variant) {
      const state = this.v2Adapter.syncPurchaseInputs({
        variantId: variant.id,
        sellingPlanId: this.subscribe && subscriptionValid ? selectedPlan.id : null,
        quantity: this.qty,
      });
      this.qty = state.value;
    } else {
      this.#ensureSellingPlanInput();
      const planInput = this.nativeForm && this.nativeForm.querySelector('input[name="selling_plan"]');
      if (planInput) planInput.value = this.subscribe && subscriptionValid ? selectedPlan.id : '';
      // Write the variant id synchronously too (mirrors Horizon's async picker write),
      // closing the race where a fast option-change → ATC could submit a stale id.
      const idInput = this.nativeForm && this.nativeForm.querySelector('input[name="id"]');
      if (idInput && variant) { idInput.value = variant.id; idInput.dataset.pogiSync = '1'; } // POGI: marks this form synchronously managed (product-form #getIntendedVariantId)
    }
    renderPdpUsage(this, {
      recommendationAllowed: quantityState?.canAdd !== false,
      enabled: Boolean(variant?.available),
      unitsPerPack: units, quantity: this.qty, perDay: this.getDailyUsage(),
      subscribed: this.subscribe && subscriptionValid, locale: this.v2Contract?.market?.displayLocale,
      maxUnit: 'months',
    });

    const atc = this.querySelector('[data-pogi-atc]');
    const atcLabel = this.querySelector('[data-pogi-atc-label]');
    let identityPrice = '';
    if (!variant && atc) {
      atc.disabled = true;
      if (atcLabel) atcLabel.textContent = this.t('js_sold_out', 'Unavailable');
    } else if (variant && atc) {
      const invalidSubscription = this.subscribe && !subscriptionValid;
      const invalidQuantity = quantityState ? variant.available && !quantityState.canAdd : false;
      atc.disabled = !variant.available || invalidSubscription || invalidQuantity;
      const unitPrice = this.subscribe && subscriptionValid ? allocation.price : variant.price;
      const priceStr = this.money(unitPrice * this.qty);
      identityPrice = priceStr;
      atcLabel.textContent = invalidSubscription || invalidQuantity
        ? this.t('js_sold_out', 'Unavailable')
        : variant.available
          ? this.t('js_atc_add', 'Add to cart · ') + priceStr
          : this.t('js_sold_out', 'Sold out');
    }

    syncPdpIdentity(this, { price: identityPrice, available: Boolean(atc && !atc.disabled),
      subscribed: this.subscribe && subscriptionValid, quantity: this.qty });

    // free-shipping nudge — subscriptions always ship free; one-time counts toward the threshold
    const ship = this.querySelector('[data-pogi-ship]');
    if (ship && variant) {
      const msg = ship.querySelector('[data-pogi-ship-msg]');
      const fill = ship.querySelector('[data-pogi-ship-fill]');
      if (this.I?.js_shipping_supported === false) {
        ship.classList.remove('is-unlocked');
        if (msg) msg.innerHTML = this.t('js_ship_always_html', '<strong>See delivery options at checkout.</strong>');
        if (fill) fill.style.width = '0%';
      } else if (this.subscribe && subscriptionValid) {
        ship.classList.add('is-unlocked');
        if (msg) msg.innerHTML = this.t('js_ship_always_html', '<strong>Free shipping, always.</strong>');
        if (fill) fill.style.width = '100%';
      } else {
        const total = variant.price * this.qty;
        const remaining = Math.max(0, this.shipThreshold - total);
        if (remaining === 0) {
          ship.classList.add('is-unlocked');
          if (msg) msg.innerHTML = this.t('js_ship_unlocked_html', '<strong>Free shipping unlocked.</strong>');
        } else {
          ship.classList.add('is-unlocked');
          if (msg) msg.innerHTML = this.t('js_ship_remaining_html', "You're <strong>[[amount]]</strong> from free shipping.", { amount: this.money(remaining) });
        }
        if (fill) fill.style.width = '100%';
      }
    }
    // Roving tabindex LAST — after the pill display-sync and the [hidden] panel
    // toggles above, so a hidden/disabled radio is never left holding a group's
    // tab stop (Codex countersign #19, 2026-07-23).
    this.#rove();
    this.#recordV2Observation();
  }

  #ensureSellingPlanInput() {
    if (!this.nativeForm) return;
    if (!this.nativeForm.querySelector('input[name="selling_plan"]')) {
      const i = document.createElement('input'); i.type = 'hidden'; i.name = 'selling_plan'; this.nativeForm.appendChild(i);
    }
  }
  isSizingFormatAvailable(key) {
    const format = this.formats.find((value) => this.#formatKey(value) === key);
    const selectedPlan = this.plan();
    // Match the exact count #setFormat would select before changing any state.
    const size = format && this.#defaultSize(format, this.pill);
    const candidate = size && this.variantObj(size, format, this.tile);
    const quantity = candidate && this.v2Adapter?.quantityState(candidate.id, this.qty);
    return Boolean(candidate?.available
      && (!this.subscribe || this.#allocationFor(candidate, selectedPlan))
      && (!quantity || (quantity.value === this.qty && quantity.canAdd)));
  }
  isSizingCountAvailable(value) {
    const candidate = this.variantObj(value);
    const quantity = candidate && this.v2Adapter?.quantityState(candidate.id, this.qty);
    return Boolean(candidate?.available && this.cardPrice(candidate, this.plan())
      && (!quantity || (quantity.value === this.qty && quantity.canAdd)));
  }
  // Public bridge used only by the PDP bag calculator. Selection travels through
  // the same option button as a shopper click; quantity is preserved;
  // Format, Scent, purchase mode and selling-plan choice remain untouched. The
  // calculator only selects a pack. The shopper reviews it before using ATC.
  applySizingRecommendation(value) {
    const option = [...this.querySelectorAll('[data-pogi-size]')]
      .find((button) => button.dataset.pogiSize === value && button.style.display !== 'none');
    if (!option || !this.isSizingCountAvailable(value)) return false;
    const intent = {
      quantity: this.qty,
      subscribe: Boolean(this.subscribe),
      planId: this.plan()?.id ?? null,
      nativePlanId: this.nativeForm?.querySelector('input[name="selling_plan"]')?.value || '',
      format: this.#formatKey(this.format),
      scent: this.tile,
    };
    option.click();
    const intentPreserved = (
      this.qty === intent.quantity
      && Boolean(this.subscribe) === intent.subscribe
      && this.#formatKey(this.format) === intent.format
      && this.tile === intent.scent
      && (!intent.subscribe || (
        String(this.plan()?.id ?? '') === String(intent.planId ?? '')
        && (this.nativeForm?.querySelector('input[name="selling_plan"]')?.value || '') === intent.nativePlanId
      ))
    );
    return intentPreserved;
  }
  #changeQty(direction) {
    const variant = this.currentVariant();
    if (this.v2Mode === 'active' && this.v2Model && !variant) {
      this.#failV2(new Error('Cannot change quantity without one resolved Bags variant'));
      return;
    }
    if (this.v2Mode === 'active' && this.v2Model) {
      this.qty = this.v2Adapter.stepQuantity(variant.id, this.qty, direction).value;
    } else {
      this.qty = Math.max(1, this.qty + (direction < 0 ? -1 : 1));
    }
    this.#syncQty();
  }
  #syncQty() {
    const out = this.querySelector('[data-pogi-qty]');
    if (out) out.textContent = this.qty;
    if (this.v2Mode === 'active' && this.v2Adapter) {
      const variant = this.currentVariant();
      const selectedPlan = this.subscribe ? this.plan() : null;
      if (variant) {
        const state = this.v2Adapter.syncPurchaseInputs({
          variantId: variant.id,
          sellingPlanId: selectedPlan?.id ?? null,
          quantity: this.qty,
        });
        this.qty = state.value;
        if (out) out.textContent = this.qty;
      }
    } else {
      const nq = this.nativeForm && this.nativeForm.querySelector('input[name="quantity"]');
      if (nq) { nq.value = this.qty; nq.dispatchEvent(new Event('change', { bubbles: true })); }
    }
    this.update();
  }
}

if (!customElements.get('pogi-buy-box-bags')) {
  customElements.define('pogi-buy-box-bags', PogiBuyBoxBags);
}
